import React, { useState, useEffect } from 'react';
import { message, notification, Badge } from 'antd';
import { WifiOutlined, DisconnectOutlined, SyncOutlined } from '@ant-design/icons';

const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showStatus, setShowStatus] = useState(false);

  useEffect(() => {
    const handleNetworkChange = (event) => {
      const online = event.detail.isOnline;
      setIsOnline(online);
      
      if (!online) {
        setShowStatus(true);
        notification.warning({
          message: '📴 You\'re Offline',
          description: 'Your data will be saved locally and synced when connection returns.',
          duration: 4,
          placement: 'topRight'
        });
      } else {
        setShowStatus(false);
        notification.success({
          message: '🌐 Back Online',
          description: 'Connection restored! Syncing your data...',
          duration: 3,
          placement: 'topRight'
        });
      }
    };

    // Listen for network status changes
    window.addEventListener('networkChange', handleNetworkChange);

    // Show offline status immediately if offline
    if (!navigator.onLine) {
      setShowStatus(true);
    }

    return () => {
      window.removeEventListener('networkChange', handleNetworkChange);
    };
  }, []);

  // Global notification handlers
  useEffect(() => {
    window.showNetworkStatus = (online) => {
      setIsOnline(online);
      if (!online) {
        setShowStatus(true);
      } else {
        setShowStatus(false);
      }
    };

    window.showSyncSuccess = (message) => {
      notification.success({
        message: '✅ Data Synced',
        description: message,
        duration: 3,
        placement: 'topRight'
      });
    };

    window.showUpdateAvailable = () => {
      notification.info({
        message: '🔄 Update Available',
        description: 'A new version is available. Please refresh to update.',
        duration: 0, // Don't auto-close
        placement: 'topRight',
        btn: (
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Now
          </button>
        )
      });
    };

    return () => {
      delete window.showNetworkStatus;
      delete window.showSyncSuccess;
      delete window.showUpdateAvailable;
    };
  }, []);

  if (!showStatus && isOnline) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '70px',
        right: '20px',
        zIndex: 2000,
        background: isOnline ? '#f6ffed' : '#fff2e8',
        border: `1px solid ${isOnline ? '#b7eb8f' : '#ffb347'}`,
        borderRadius: '8px',
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: '12px',
        maxWidth: '200px'
      }}
    >
      <Badge 
        status={isOnline ? 'success' : 'warning'} 
        dot 
        style={{ 
          marginRight: '4px'
        }}
      />
      
      {isOnline ? (
        <>
          <WifiOutlined style={{ color: '#52c41a' }} />
          <span style={{ color: '#52c41a', fontWeight: '500' }}>
            Online
          </span>
        </>
      ) : (
        <>
          <DisconnectOutlined style={{ color: '#fa8c16' }} />
          <span style={{ color: '#fa8c16', fontWeight: '500' }}>
            Offline Mode
          </span>
        </>
      )}
      
      {!isOnline && (
        <SyncOutlined 
          spin 
          style={{ color: '#fa8c16', marginLeft: '4px' }} 
          title="Waiting to sync..."
        />
      )}
    </div>
  );
};

export default NetworkStatus; 